import React from "react";
import Asset3 from "@/assets/images/asset3.svg?react";
import Asset5 from "@/assets/images/asset5.svg?react";
import Asset7 from "@/assets/images/asset7.svg?react";
import DashboardOutlinedIcon from "@mui/icons-material/DashboardOutlined";
import LinkIcon from "@mui/icons-material/Link";
import TableChartOutlinedIcon from "@mui/icons-material/TableChartOutlined";
import {
  delay,
  perimeter,
  timed,
  traced,
  vars,
  type Box,
} from "@/components/guide/story";
import { FIRST_ROW, HOLDER } from "./boxes";
import "@/components/guide/story.css";

// The story's close-ups. A list is swept, never chosen: moving on chooses its first row.
type Icon = React.FC<React.SVGProps<SVGSVGElement>>;

/** A box that traces its outline, then fills in behind it. */
export function Frame({
  x,
  y,
  w,
  h,
  fill,
  at,
  className,
}: Box & {
  fill: string;
  at: number;
  className?: string;
}) {
  return (
    <g className={className}>
      <rect
        className="og-fade"
        style={delay(at + 150)}
        x={x}
        y={y}
        width={w}
        height={h}
        fill={fill}
      />
      <rect
        className="og-trace"
        style={traced(perimeter(w, h), at)}
        x={x}
        y={y}
        width={w}
        height={h}
        fill="none"
        stroke="var(--color-border)"
      />
    </g>
  );
}

export function Holder({ row }: { row: Row }) {
  return (
    <>
      <Frame
        {...HOLDER}
        fill="var(--color-bg-subtle)"
        at={0}
        className="og-holder"
      />
      {/* The row it opened from, as the same line at its head. */}
      <g className="og-rise og-title" style={delay(250)}>
        <Line row={row} baseline={38} />
      </g>
      <line
        className="og-trace"
        style={traced(248, 300)}
        x1="36"
        x2="284"
        y1="52"
        y2="52"
        stroke="var(--color-border)"
      />
    </>
  );
}

const MODELS: {
  name: string;
  relation: string;
  opened: string;
  icon: Icon;
  color: string;
}[] = [
  {
    name: "Sales",
    relation: "Owner",
    opened: "16m",
    icon: Asset3,
    color: "var(--color-asset-sky)",
  },
  {
    name: "Inventory",
    relation: "Owner",
    opened: "2h",
    icon: Asset5,
    color: "var(--color-asset-aurora)",
  },
  {
    name: "Pipeline",
    relation: "Shared",
    opened: "3d",
    icon: Asset7,
    color: "var(--color-asset-ember)",
  },
];

const ROW = FIRST_ROW;
const rowStart = (i: number) => 550 + i * 150;

// A row says what it holds and no more: a space its models, a table its
// shape. A model's is the Overview's own, compressed.
interface Row {
  name: string;
  icon: React.ReactNode;
  /** After the name, as a model row says whose it is. */
  label?: string;
  /** At the row's end in the app's mono: a space's model count, dimmed as the
   * rail draws it, when a model was opened, or a table's shape. */
  figure?: string;
  dimFigure?: boolean;
  /** A model's space, its dot before the time. */
  space?: string;
}

const PITCH = ROW.h + 6;
const SCAN_H = 40;
const SCAN_MS = 900;

/** A soft band sweeping down the list once: what the space holds, taken in. */
function Scan({ count, at }: { count: number; at: number }) {
  return (
    <g className="og-transient" clipPath="url(#og-scan-clip)">
      <defs>
        <linearGradient id="og-scan" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="var(--color-accent)" stopOpacity="0" />
          <stop
            offset="0.6"
            stopColor="var(--color-accent)"
            stopOpacity="0.22"
          />
          <stop offset="1" stopColor="var(--color-accent)" stopOpacity="0" />
        </linearGradient>
        <clipPath id="og-scan-clip">
          <rect x={ROW.x} y={ROW.y} width={ROW.w} height={count * PITCH - 6} />
        </clipPath>
      </defs>
      <g
        className="og-move"
        style={{
          ...vars({
            "--og-dx": "0px",
            "--og-dy": `${count * PITCH + SCAN_H}px`,
          }),
          ...timed(at, SCAN_MS),
        }}
      >
        <rect
          x={ROW.x}
          y={ROW.y - SCAN_H}
          width={ROW.w}
          height={SCAN_H}
          fill="url(#og-scan)"
        />
      </g>
    </g>
  );
}

const END = ROW.x + ROW.w - 10;
const TIME_W = 28; // the Overview's time column, compressed

/** A row's line on its text baseline, as a list row and as a panel's head. */
function Line({ row, baseline }: { row: Row; baseline: number }) {
  return (
    <>
      <g transform={`translate(${ROW.x + 8} ${baseline - 12})`}>{row.icon}</g>
      <text
        className="og-t og-medium"
        x={ROW.x + 32}
        y={baseline}
        fill="var(--color-fg)"
      >
        {row.name}
        {row.label && (
          <tspan
            dx="8"
            className="og-t-sm og-regular"
            fill="var(--color-fg-muted)"
          >
            {row.label}
          </tspan>
        )}
      </text>
      {row.space && (
        <rect
          x={END - TIME_W - 14}
          y={baseline - 7}
          width="6"
          height="6"
          fill={row.space}
        />
      )}
      <text
        className="og-mono og-t-xs"
        x={END}
        y={baseline}
        textAnchor="end"
        fill="var(--color-fg-muted)"
        opacity={row.dimFigure ? 0.7 : undefined}
      >
        {row.figure}
      </text>
    </>
  );
}

function RowLine({ row, y }: { row: Row; y: number }) {
  return (
    <g className="og-row-title">
      <Line row={row} baseline={y + ROW.h / 2 + 4} />
    </g>
  );
}

/** Rows as the app lists them, shown one by one and swept once. The first is
 * `og-landing`: coming back, the box folds into it. */
function Rows({ rows }: { rows: Row[] }) {
  return (
    <>
      <Scan count={rows.length} at={rowStart(rows.length) + 100} />
      {rows.map((row, i) => (
        <g
          key={row.name}
          className={i === 0 ? "og-rise og-landing" : "og-rise"}
          style={delay(rowStart(i))}
        >
          <RowLine row={row} y={ROW.y + i * PITCH} />
        </g>
      ))}
    </>
  );
}

/** A space's dot as the app draws it (`SpaceDot` md), in a 16px icon slot. */
export function SpaceDot({
  color,
  x = 0,
  y = 0,
}: {
  color: string;
  x?: number;
  y?: number;
}) {
  return <rect x={x + 4} y={y + 4} width="8" height="8" fill={color} />;
}

const SPACES: Row[] = [
  {
    name: "Your space",
    icon: <SpaceDot color="var(--color-cat-sky)" />,
    figure: "3",
    dimFigure: true,
  },
  {
    name: "Marketing",
    icon: <SpaceDot color="var(--color-cat-orchid)" />,
    figure: "5",
    dimFigure: true,
  },
  {
    name: "Finance",
    icon: <SpaceDot color="var(--color-cat-ember)" />,
    figure: "2",
    dimFigure: true,
  },
];

/** The spaces you belong to: yours, and one per team of your organization. */
export function SpacesScene(): React.JSX.Element {
  return (
    <>
      <Holder
        row={{
          name: "Spaces",
          icon: (
            // Sized by attribute: a nested <svg> ignores CSS width and height.
            <DashboardOutlinedIcon
              width="16"
              height="16"
              style={{ color: "var(--color-fg-muted)" }}
            />
          ),
        }}
      />
      <Rows rows={SPACES} />
    </>
  );
}

const MODEL_ROWS: Row[] = MODELS.map((model) => ({
  name: model.name,
  icon: (
    <model.icon
      width="16"
      height="16"
      className="icon-svg"
      style={vars({ "--icon-color": model.color })}
    />
  ),
  label: model.relation,
  figure: model.opened,
  space: "var(--color-cat-sky)",
}));

/** A space holds models, listed as its page lists them. */
export function SpaceScene(): React.JSX.Element {
  return (
    <>
      <Holder row={SPACES[0]} />
      <Rows rows={MODEL_ROWS} />
    </>
  );
}

const TABLES: Row[] = [
  { name: "Customers", figure: "4 columns · 1.2k rows" },
  { name: "Orders", figure: "6 columns · 8.4k rows" },
  { name: "Products", figure: "5 columns · 310 rows" },
].map((table) => ({
  ...table,
  icon: (
    // Sized by attribute: a nested <svg> ignores CSS width and height.
    <TableChartOutlinedIcon
      width="16"
      height="16"
      style={{ color: "var(--color-fg-muted)" }}
    />
  ),
}));

/** A model holds tables, listed as its canvas's directory lists them. */
export function ModelScene(): React.JSX.Element {
  return (
    <>
      <Holder row={MODEL_ROWS[0]} />
      <Rows rows={TABLES} />
    </>
  );
}

const MODEL: Box = { x: 40, y: 42, w: 240, h: 102 };
const TABLE_BOXES: Box[] = [68, 188].map((x) => ({ x, y: 66, w: 64, h: 54 }));
const HEADER_H = 14;
const ICON = 16;
const ICON_GAP = 3;

function Outline({
  box,
  at,
  stroke,
}: {
  box: Box;
  at: number;
  stroke: string;
}) {
  return (
    <rect
      className="og-trace"
      style={traced(perimeter(box.w, box.h), at)}
      x={box.x}
      y={box.y}
      width={box.w}
      height={box.h}
      fill="none"
      stroke={stroke}
    />
  );
}

/** A label set into its box's top edge; the halo in the picture's ground cuts the line. */
function Legend({
  box,
  at,
  text,
  fill,
}: {
  box: Box;
  at: number;
  text: string;
  fill: string;
}) {
  return (
    <text
      className="og-rise og-t-sm og-medium"
      style={delay(at)}
      x={box.x + 10}
      y={box.y}
      dominantBaseline="central"
      fill={fill}
      stroke="var(--color-bg)"
      strokeWidth="6"
      strokeLinejoin="round"
      paintOrder="stroke"
    >
      {text}
    </text>
  );
}

/** The whole nesting as a schematic: a space holds a model, a model holds linked tables. */
export function CoverScene(): React.JSX.Element {
  const [left, right] = TABLE_BOXES;
  const linkY = left.y + HEADER_H + (left.h - HEADER_H) / 2;
  const linkX = (left.x + left.w + right.x) / 2;
  const gap = ICON / 2 + ICON_GAP;
  return (
    <>
      <rect
        className="og-fade"
        x={HOLDER.x}
        y={HOLDER.y}
        width={HOLDER.w}
        height={HOLDER.h}
        fill="none"
        stroke="var(--color-border)"
        strokeDasharray="3 3"
      />
      <Legend box={HOLDER} at={150} text="Space" fill="var(--color-fg-muted)" />
      <Outline box={MODEL} at={250} stroke="var(--color-border)" />
      <Legend box={MODEL} at={500} text="Model" fill="var(--color-fg-muted)" />
      {TABLE_BOXES.map((box, i) => (
        <React.Fragment key={box.x}>
          <Outline box={box} at={650 + i * 150} stroke="var(--color-accent)" />
          <line
            className="og-trace"
            style={traced(box.w, 900 + i * 150)}
            x1={box.x}
            x2={box.x + box.w}
            y1={box.y + HEADER_H}
            y2={box.y + HEADER_H}
            stroke="var(--color-accent)"
            opacity="0.5"
          />
          <Legend
            box={box}
            at={900 + i * 150}
            text="Table"
            fill="var(--color-accent)"
          />
        </React.Fragment>
      ))}
      <path
        className="og-draw"
        style={delay(1250)}
        d={`M${left.x + left.w},${linkY} H${linkX - gap} M${linkX + gap},${linkY} H${right.x}`}
        pathLength={1}
        fill="none"
        stroke="var(--color-accent)"
        strokeWidth="1.5"
      />
      <g className="og-pop" style={delay(1650)}>
        <g transform={`translate(${linkX - ICON / 2} ${linkY - ICON / 2})`}>
          <LinkIcon
            width={ICON}
            height={ICON}
            style={{ color: "var(--color-accent)" }}
          />
        </g>
      </g>
    </>
  );
}
